import { useEffect, useRef, useState } from 'react';

/** Wait this long after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 300;
/** Fewer characters than this never searches. */
const MIN_SEARCH_CHARS = 2;

/** How a record shows in the lists: its number, and its job name if it has one. */
export interface RecordLabel {
  title: string;
  subtitle?: string | null;
}

/**
 * Search-as-you-type for a record (RecordPicker's search box, and any form
 * that needs only that part, e.g. Measure2Quote's Open a job). Debounces
 * keystrokes (each search costs the API an entitlement check and a Dataverse
 * query), and never lets an older, slower answer overwrite a newer one.
 * `fill` puts a picked record's text in the box without searching again.
 */
export function useRecordSearch<S>(search: (term: string) => Promise<S[]>) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<S[]>([]);
  const [searching, setSearching] = useState(false);
  /** The term the current hits answer, or null before an answer; `failed` if that search threw. */
  const [answered, setAnswered] = useState<{ term: string; failed: boolean } | null>(null);
  const latest = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    // Set on setup too: StrictMode runs setup → cleanup → setup in development,
    // and a ref left false would discard every search result.
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  /** Put `term` in the box with no hits, forgetting any search in flight or waiting. */
  function fill(term: string) {
    setQuery(term);
    if (timer.current) clearTimeout(timer.current);
    ++latest.current;
    setHits([]);
    setSearching(false);
    setAnswered(null);
  }

  function onQueryChange(term: string) {
    setQuery(term);
    if (timer.current) clearTimeout(timer.current);
    const seq = ++latest.current;
    if (term.trim().length < MIN_SEARCH_CHARS) {
      setHits([]);
      setSearching(false);
      setAnswered(null);
      return;
    }
    setSearching(true);
    const isCurrent = () => mounted.current && seq === latest.current;
    timer.current = setTimeout(() => {
      search(term)
        .then((result) => {
          if (isCurrent()) {
            setHits(result);
            setAnswered({ term, failed: false });
          }
        })
        .catch(() => {
          if (isCurrent()) {
            setHits([]);
            setAnswered({ term, failed: true });
          }
        })
        .finally(() => {
          if (isCurrent()) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
  }

  return { query, hits, searching, answered, onQueryChange, fill };
}

/** Search hits as a list of buttons: the record's number, then its name. */
export function RecordSearchResults<S extends { id: string }>({
  items,
  label,
  onPick,
}: {
  items: S[];
  label: (item: S) => RecordLabel;
  onPick: (item: S) => void;
}) {
  return (
    <ul className="max-h-80 overflow-hidden overflow-y-auto rounded-lg border border-slate-200 bg-white">
      {items.map((item) => {
        const { title, subtitle } = label(item);
        return (
          <li key={item.id} className="border-t border-slate-100 first:border-t-0">
            <button
              type="button"
              onClick={() => onPick(item)}
              className="flex w-full items-center gap-2 px-3 py-3 text-left hover:bg-slate-50"
            >
              <span className="text-sm font-medium text-slate-900">{title}</span>
              {subtitle && <span className="truncate text-sm text-slate-500">{subtitle}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
